import { Bird, Bug, Leaf, Network, PawPrint, Sprout } from 'lucide-react';
import { useState } from 'react';
import { ecologyNodes } from '../data';

const links = [
  { from: 'grass', to: 'insects', label: '取食' },
  { from: 'grass', to: 'grazers', label: '取食' },
  { from: 'insects', to: 'birds', label: '捕食' },
  { from: 'grazers', to: 'soil', label: '粪便与枯草' },
  { from: 'soil', to: 'grass', label: '养分回到土壤' },
  { from: 'birds', to: 'soil', label: '残羽与粪便' }
];

const nodeVisuals = {
  grass: { icon: Leaf, tone: 'grass', shortRole: '生产' },
  insects: { icon: Bug, tone: 'amber', shortRole: '取食' },
  birds: { icon: Bird, tone: 'sky', shortRole: '捕食' },
  grazers: { icon: PawPrint, tone: 'berry', shortRole: '放牧' },
  soil: { icon: Sprout, tone: 'soil', shortRole: '分解' }
};

type VisualId = keyof typeof nodeVisuals;

function findNode(id: string) {
  return ecologyNodes.find((node) => node.id === id);
}

function getVisual(id: string) {
  return nodeVisuals[id as VisualId] ?? nodeVisuals.grass;
}

function getCurve(fromId: string, toId: string, index: number) {
  const from = findNode(fromId);
  const to = findNode(toId);
  if (!from || !to) return '';

  const midX = (from.x + to.x) / 2;
  const midY = (from.y + to.y) / 2;
  const lift = index % 2 === 0 ? -12 : 12;
  const drift = from.x < to.x ? 7 : -7;

  return `M ${from.x} ${from.y} Q ${midX + drift} ${midY + lift}, ${to.x} ${to.y}`;
}

export function EcologyWeb() {
  const [activeNode, setActiveNode] = useState(ecologyNodes[0]);
  const activeVisual = getVisual(activeNode.id);
  const ActiveIcon = activeVisual.icon;

  return (
    <section className="ecology-panel ecology-atlas">
      <div className="panel-heading">
        <Network aria-hidden="true" />
        <div>
          <h2>点一点：草原食物网</h2>
          <p>草、昆虫、鸟、牛羊马和土壤，并不是分开的。</p>
        </div>
      </div>

      <div className="web-board ecosystem-board">
        <svg viewBox="0 0 100 100" aria-hidden="true">
          <defs>
            <linearGradient id="webGradient" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0%" stopColor="#d8ead0" />
              <stop offset="55%" stopColor="#eef2da" />
              <stop offset="100%" stopColor="#d4e7ec" />
            </linearGradient>
            <marker id="webArrow" markerHeight="5" markerWidth="5" orient="auto" refX="4" refY="2.5">
              <path d="M0,0 L5,2.5 L0,5 Z" fill="#5f8c63" />
            </marker>
          </defs>
          <rect fill="url(#webGradient)" height="100" rx="8" width="100" />
          <path className="web-land-line" d="M2 78 C18 70, 32 83, 49 74 C64 65, 82 78, 98 66" />
          <path className="web-water-line" d="M6 25 C22 33, 32 18, 48 24 C63 30, 72 19, 94 27" />
          {links.map((link, index) => {
            const isActive = link.from === activeNode.id || link.to === activeNode.id;
            return (
              <path
                className={isActive ? 'web-edge active' : 'web-edge'}
                d={getCurve(link.from, link.to, index)}
                key={`${link.from}-${link.to}`}
                markerEnd="url(#webArrow)"
              />
            );
          })}
        </svg>

        {ecologyNodes.map((node) => {
          const visual = getVisual(node.id);
          const Icon = visual.icon;
          return (
            <button
              className={node.id === activeNode.id ? `web-node tone-${visual.tone} active` : `web-node tone-${visual.tone}`}
              key={node.id}
              onClick={() => setActiveNode(node)}
              style={{ left: `${node.x}%`, top: `${node.y}%` }}
              type="button"
            >
              <Icon aria-hidden="true" />
              <span>{node.name}</span>
              <small>{visual.shortRole}</small>
            </button>
          );
        })}
      </div>

      <article className={`web-detail tone-${activeVisual.tone}`}>
        <ActiveIcon aria-hidden="true" />
        <div>
          <b>{activeNode.name} · {activeNode.role}</b>
          <p>{activeNode.detail}</p>
        </div>
      </article>

      <div className="web-legend" aria-label="Food web legend">
        <span>能量从植物开始</span>
        <span>昆虫和草食动物取食</span>
        <span>分解者让养分回到土壤</span>
      </div>
    </section>
  );
}
